import {
  DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_TYPE_LABELS,
} from "../onboarding/constants";
import { verificationFieldLabel } from "./RyftNonHostedDashboard.labels";
import type { RyftAccountGetResponseType } from "@sellar-io/api-v2/types";

export type RyftBusinessError = {
  type:
    | "bank-account"
    | "business-details"
    | "director-details"
    | "business-documents"
    | "director-documents";
  message: string;
};

export const getRyftVerificationStatuses = (
  account: RyftAccountGetResponseType,
) => ({
  business: account.verification.status,
  director: account.directors[0]?.status,
});

export const getRyftBusinessErrors = (
  account: RyftAccountGetResponseType,
): { errors: RyftBusinessError[] } => {
  const errors: RyftBusinessError[] = [];

  const addError = (type: RyftBusinessError["type"], message: string) => {
    if (
      errors.some((error) => error.type === type && error.message === message)
    ) {
      return;
    }
    errors.push({ type, message });
  };

  const addVerificationErrors = (
    verification: RyftAccountGetResponseType["verification"],
    documents: RyftAccountGetResponseType["uploadedDocuments"],
    detailsType: "business-details" | "director-details",
    documentsType: "business-documents" | "director-documents",
  ) => {
    for (const error of verification.errors ?? []) {
      if (error.code === "InvalidDocument") {
        addError(documentsType, error.description);
      } else {
        addError(detailsType, error.description);
      }
    }

    for (const field of verification.requiredFields ?? []) {
      const label = verificationFieldLabel(field.name);
      addError(detailsType, `Provide ${label.toLowerCase()}.`);
    }

    for (const request of verification.requiredDocuments ?? []) {
      const category = DOCUMENT_CATEGORY_LABELS[request.category].toLowerCase();

      let documentLabel = "documents";

      if (request.quantity === 1) {
        documentLabel = "document";
      }

      addError(
        documentsType,
        `Upload ${request.quantity} ${category} ${documentLabel}.`,
      );
    }

    for (const document of documents) {
      if (document.invalidReason) {
        addError(documentsType, document.invalidReason);
      } else if (document.status === "Invalid") {
        const label = DOCUMENT_TYPE_LABELS[document.type].toLowerCase();
        addError(documentsType, `Replace the invalid ${label} document.`);
      }
    }
  };

  addVerificationErrors(
    account.verification,
    account.uploadedDocuments,
    "business-details",
    "business-documents",
  );

  for (const director of account.directors) {
    addVerificationErrors(
      director.verification,
      director.documents ?? [],
      "director-details",
      "director-documents",
    );
  }

  for (const method of account.payoutMethods) {
    if (method.invalidReason) {
      addError("bank-account", method.invalidReason);
    } else if (method.status === "Invalid") {
      addError("bank-account", "Review the invalid bank account details.");
    }
  }

  return { errors };
};
